"use client";

import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  BadgeCheck,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Clock,
  Coins,
  Cpu,
  type LucideIcon,
  TrendingUp,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

import { Container } from "@/app/(marketing)/_components/ui/container";

const BARCODE_BARS = [
  2, 1, 3, 1, 2, 1, 1, 3, 2, 1, 3, 2, 1, 1, 2, 3, 1, 2, 1, 3, 2, 1, 2, 1, 3, 1,
  2, 3, 1, 2, 1, 3, 1, 2, 1, 3, 2, 1, 3, 1, 2, 1, 3, 2, 1, 2, 1, 3, 2, 1, 2, 1,
  3, 1, 2, 1, 1, 3, 2, 1, 3, 2, 1, 1, 2, 3, 1, 2, 1, 3, 2, 1, 2,
];

interface ExpertStat {
  label: string;
  value: string;
  icon: LucideIcon;
}

interface ExpertTestimonial {
  name: string;
  role: string;
  company: string;
  location: string;
  flag: string;
  photoSrc: string;
  quote: string;
  tierLabel: string;
  serial: string;
  attestations: string[];
  stats: ExpertStat[];
  storyHref?: string;
  storyCta?: string;
}

const TESTIMONIALS: ExpertTestimonial[] = [
  {
    name: "Kritika Gupta",
    role: "Finance & AI Evaluation Specialist",
    company: "",
    location: "United States",
    flag: "🇺🇸",
    photoSrc: "/testimonials/kritika-gupta.jpg",
    quote:
      "Before joining, I never imagined there could be such an exciting intersection between AI and finance. And I have to admit, the $200/hour projects caught my attention. It genuinely felt like my skills and experience were being valued.",
    tierLabel: "Finance Expert",
    serial: "TR-KG26-FIN1",
    attestations: ["ACCT", "FP&A", "TAX", "AI"],
    stats: [
      { label: "Earned", value: "$8,500+", icon: Coins },
      { label: "Field", value: "Finance & AI Evals", icon: TrendingUp },
      { label: "Cadence", value: "~10-20 hr/wk", icon: Clock },
    ],
  },
  {
    name: "Megan Boyer",
    role: "Accounting & AI Training Specialist",
    company: "Terac Community Leader",
    location: "Remote",
    flag: "🌐",
    photoSrc: "/testimonials/megan-boyer.jpg",
    quote:
      "Since transitioning from owning my construction and building company, Terac has let me apply my background in accounting, business management, and marketing to high-impact projects. The flexibility and community collaboration make every hour well worth my time.",
    tierLabel: "Community Leader",
    serial: "TR-MB26-CL01",
    attestations: ["ACCT", "BIZ", "MKTG", "AI"],
    stats: [
      { label: "Multiple", value: "PROJECTS", icon: CheckCheck },
      { label: "Field", value: "Accounting & AI", icon: TrendingUp },
      { label: "Cadence", value: "1 hr to full-time", icon: Clock },
    ],
  },
  {
    name: "Nikita Annenkov",
    role: "ML Systems Researcher",
    company: "",
    location: "United States",
    flag: "🇺🇸",
    photoSrc: "/testimonials/nikita-annenkov.jpg",
    quote:
      "The most interesting part so far has been how much the work rewards precision. A good task is not just hard, it has to be fair, deterministic, reviewable, and calibrated so that an agent's failure actually means something.",
    tierLabel: "ML Systems Contributor",
    serial: "TR-NA26-ML01",
    attestations: ["ML", "AI", "SYS", "EVAL"],
    stats: [
      { label: "Multiple", value: "AI EVALS", icon: CheckCheck },
      { label: "Field", value: "ML Systems & AI Eval", icon: Cpu },
      { label: "Cadence", value: "Flexible async", icon: Clock },
    ],
    storyHref: "/stories/nikita-annenkov",
    storyCta: "Read Nikita's story",
  },
  {
    name: "Manizha Said",
    role: "CPA, Accounting & Finance Expert",
    company: "",
    location: "United States",
    flag: "🇺🇸",
    photoSrc: "/testimonials/manizha-said.jpg",
    quote:
      "I take on complex accounting and finance projects involving financial reporting, reconciliations, variance analysis, and NetSuite. I especially appreciate the variety of assignments, the chance to contribute my expertise, and the competitive compensation.",
    tierLabel: "Finance Expert",
    serial: "TR-MS26-CPA1",
    attestations: ["CPA", "ACCT", "FIN", "OPS"],
    stats: [
      { label: "Earned", value: "$9,000+", icon: Coins },
      { label: "Field", value: "Accounting & Finance", icon: TrendingUp },
      { label: "Cadence", value: "40+ hr/wk", icon: Clock },
    ],
  },
  {
    name: "Laura Chen",
    role: "AI Review Accounting Expert",
    company: "CPA, MBA, Ex-Big 4 Audit",
    location: "Remote",
    flag: "🌐",
    photoSrc: "/testimonials/laura-chen.jpg",
    quote:
      "Terac involves you in projects that fit your experience closely. I enjoyed the accounting project reviewing and modifying an AI accounting workflow, which is very similar to my previous work as VP Global Controller and Big 4 Audit Manager.",
    tierLabel: "Accounting Expert",
    serial: "TR-LC26-CPA2",
    attestations: ["CPA", "MBA", "AUDIT", "AI"],
    stats: [
      { label: "Background", value: "BIG 4", icon: CheckCheck },
      { label: "Field", value: "AI Accounting Review", icon: TrendingUp },
      { label: "Cadence", value: "5-60 hr/wk", icon: Clock },
    ],
  },
  {
    name: "Vidhi Bhatt",
    role: "Finance AI Expert",
    company: "",
    location: "United States",
    flag: "🇺🇸",
    photoSrc: "/testimonials/vidhi-bhatt.jpg",
    quote:
      "I contribute to finance-focused model assessments, evaluating the accuracy of financial analysis, reasoning, and business judgment so AI-generated responses align with real-world professional standards.",
    tierLabel: "Finance AI Expert",
    serial: "TR-VB26-FIN2",
    attestations: ["FIN", "AI", "EVAL", "BIZ"],
    stats: [
      { label: "Cadence", value: "20+ HR/WK", icon: Clock },
      { label: "Field", value: "Finance & AI Eval", icon: TrendingUp },
      { label: "Engagement", value: "Long-term", icon: CheckCheck },
    ],
  },
];

export default function ExpertsTestimonials() {
  const [index, setIndex] = useState(0);
  const total = TESTIMONIALS.length;
  const active = TESTIMONIALS[index] ?? TESTIMONIALS[0];

  if (!active) {
    return null;
  }

  const goPrev = () => {
    setIndex((current) => (current - 1 + total) % total);
  };
  const goNext = () => {
    setIndex((current) => (current + 1) % total);
  };

  return (
    <section className="bg-marketing-cream py-16 lg:py-24">
      <Container>
        <div className="mb-10 flex flex-col items-start gap-3 sm:items-center">
          <div className="border-marketing-border bg-marketing-cream-light rounded-full border px-3 py-1">
            <p className="text-marketing-text-muted font-mono text-sm tracking-[-0.28px]">
              Voices from the panel
            </p>
          </div>
          <h2 className="text-marketing-text text-heading-32 md:text-heading-40 lg:text-heading-48 text-balance sm:text-center sm:whitespace-nowrap">
            Real experts. Real payouts. Real work.
          </h2>
          <p className="text-marketing-text-muted text-copy-16 text-balance sm:text-center sm:whitespace-nowrap">
            A few of the people powering studies, interviews, and training data
            on Terac.
          </p>
        </div>

        <div
          className="mx-auto w-full max-w-[720px]"
          role="region"
          aria-roledescription="carousel"
          aria-label="Expert testimonials"
          style={{ perspective: "1200px" }}
        >
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-300 ease-out"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {TESTIMONIALS.map((expert, i) => {
                const isActive = i === index;
                return (
                  <div
                    key={expert.name}
                    className="w-full shrink-0 grow-0 basis-full"
                    aria-hidden={!isActive}
                    inert={!isActive}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${i + 1} of ${total}: ${expert.name}`}
                  >
                    <TestimonialCard expert={expert} />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={goPrev}
              aria-label="Previous testimonial"
              className="border-marketing-border bg-marketing-cream-light text-marketing-text hover:bg-marketing-cream-hover focus-visible:ring-marketing-blue/40 flex size-10 items-center justify-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2"
            >
              <ChevronLeft className="size-5" />
            </button>

            <div
              className="flex items-center gap-2"
              role="tablist"
              aria-label="Select testimonial"
            >
              {TESTIMONIALS.map((expert, i) => {
                const isActive = i === index;
                return (
                  <button
                    key={expert.name}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-label={`Show testimonial from ${expert.name}`}
                    onClick={() => {
                      setIndex(i);
                    }}
                    className={`h-2 rounded-full transition-[width,background-color] duration-150 ease-out ${
                      isActive
                        ? "bg-marketing-blue w-8"
                        : "bg-marketing-text-muted/30 hover:bg-marketing-text-muted/50 w-2"
                    }`}
                  />
                );
              })}
            </div>

            <button
              type="button"
              onClick={goNext}
              aria-label="Next testimonial"
              className="border-marketing-border bg-marketing-cream-light text-marketing-text hover:bg-marketing-cream-hover focus-visible:ring-marketing-blue/40 flex size-10 items-center justify-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2"
            >
              <ChevronRight className="size-5" />
            </button>
          </div>
        </div>
      </Container>
    </section>
  );
}

function TestimonialCard({ expert }: { expert: ExpertTestimonial }) {
  const ctaLabel = expert.storyCta ?? "Read story";
  // The first stat is rendered big on the left ("the point is to show they
  // made money / impact"). Remaining stats sit in the small grid on the right.
  const headlineStat = expert.stats[0];
  const restStats = expert.stats.slice(1);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  // Heavier spring: bigger mass + damping + lower stiffness = deeper travel
  // that still feels glassy, never jittery.
  const springConfig = { damping: 32, stiffness: 55, mass: 1.4 };
  const springX = useSpring(mouseX, springConfig);
  const springY = useSpring(mouseY, springConfig);
  const rotateX = useTransform(springY, [-0.5, 0.5], ["13deg", "-13deg"]);
  const rotateY = useTransform(springX, [-0.5, 0.5], ["-13deg", "13deg"]);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  const Wrapper = expert.storyHref ? Link : "div";
  const wrapperProps = expert.storyHref
    ? {
        href: expert.storyHref,
        "aria-label": `${ctaLabel}, ${expert.name}, ${expert.role}`,
      }
    : {};

  return (
    <Wrapper
      {...(wrapperProps as { href: string; "aria-label": string })}
      className="focus-visible:ring-marketing-blue/40 group block rounded-[24px] focus:outline-none focus-visible:ring-2"
    >
      <motion.div
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="bg-marketing-cream relative rounded-[22px] p-1 will-change-transform"
      >
        <motion.article
          style={{
            transform: "translateZ(28px)",
            transformStyle: "preserve-3d",
            willChange: "transform",
            backfaceVisibility: "hidden",
          }}
          className="border-marketing-border bg-marketing-cream-light flex flex-col overflow-hidden rounded-[20px] border"
        >
          <div className="bg-marketing-blue flex items-center justify-between gap-4 px-5 py-3 sm:px-6 sm:py-4">
            <div className="flex items-center gap-2">
              <span className="text-marketing-cream font-mono text-[11px] tracking-[0.22em] uppercase sm:text-[12px]">
                Verified Expert
              </span>
              <BadgeCheck
                className="text-marketing-cream/90 size-4"
                strokeWidth={2.25}
                aria-hidden="true"
              />
            </div>
            {expert.storyHref ? (
              <span
                aria-hidden="true"
                className="text-marketing-cream inline-flex items-center gap-1.5 text-sm font-medium sm:text-base"
              >
                <span className="decoration-marketing-cream/60 underline-offset-4 group-hover:underline">
                  {ctaLabel}
                </span>
                <ArrowUpRight className="size-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            ) : null}
          </div>

          <div className="grid gap-6 p-6 sm:grid-cols-[220px_1fr] sm:p-8">
            <div className="flex flex-col items-start gap-4">
              <div className="flex flex-wrap items-center gap-1.5">
                {expert.attestations.map((label) => (
                  <div
                    key={label}
                    className="border-marketing-border bg-marketing-cream flex items-center gap-1 rounded-full border px-2 py-0.5"
                  >
                    <CheckGlyph />
                    <span className="text-marketing-text-secondary font-mono text-[9px] tracking-[0.12em]">
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="relative w-full pb-4">
                <div className="border-marketing-border bg-marketing-cream relative aspect-square w-full overflow-hidden rounded-[14px] border-2">
                  <Image
                    src={expert.photoSrc}
                    alt={`Portrait of ${expert.name}`}
                    fill
                    sizes="(max-width: 640px) 100vw, 220px"
                    className="object-cover"
                  />
                </div>

                {headlineStat && (
                  <div className="bg-marketing-blue absolute -bottom-1 -left-2 flex flex-col gap-0.5 rounded-[14px] px-5 py-3.5">
                    <span className="text-marketing-cream/70 font-mono text-[10px] tracking-[0.22em] uppercase">
                      {headlineStat.label}
                    </span>
                    <span className="text-heading-24 sm:text-heading-32 text-white tabular-nums">
                      {headlineStat.value}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <div className="flex items-baseline gap-2">
                  <h3 className="text-marketing-text text-heading-20 sm:text-heading-24 text-balance">
                    {expert.name}
                  </h3>
                  <span
                    className="text-base leading-none"
                    aria-label={expert.location}
                  >
                    {expert.flag}
                  </span>
                </div>
                <p className="text-marketing-text-muted font-mono text-xs tracking-[0.08em] uppercase">
                  {expert.company
                    ? `${expert.role} · ${expert.company}`
                    : expert.role}
                </p>
              </div>

              <blockquote className="text-marketing-text text-copy-16 relative border-l-2 border-[var(--color-marketing-blue)] pl-6">
                <BigQuoteGlyph />
                <span className="block pt-0.5">{expert.quote}&rdquo;</span>
              </blockquote>

              {restStats.length > 0 && (
                <div className="border-marketing-border-light grid grid-cols-2 divide-x divide-[var(--color-marketing-border-light)] rounded-[12px] border bg-white/60 px-2 py-4">
                  {restStats.map((stat) => (
                    <div
                      key={stat.label}
                      className="flex min-w-0 flex-col gap-0.5 px-3 sm:px-4"
                    >
                      <span className="text-marketing-text-muted/80 font-mono text-[10px] tracking-[0.1em] uppercase">
                        {stat.label}
                      </span>
                      <span className="text-marketing-text text-sm leading-tight font-semibold tracking-[-0.01em]">
                        {stat.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div
            className="border-marketing-border-light flex items-end gap-[1.5px] border-t px-6 py-2.5"
            aria-hidden="true"
          >
            {BARCODE_BARS.map((w, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`bar-${i}`}
                className="bg-marketing-text-muted/70"
                style={{ width: `${w}px`, height: "14px" }}
              />
            ))}
          </div>

          <div className="border-marketing-border-light bg-marketing-cream flex items-center justify-between border-t px-6 py-2.5">
            <div className="flex items-center gap-2">
              <Image
                src="/brand/logo.svg"
                alt=""
                width={12}
                height={12}
                draggable={false}
              />
              <span className="text-marketing-text-muted font-mono text-[10px] tracking-[0.18em] uppercase">
                Terac
              </span>
            </div>
            <span className="text-marketing-text-secondary font-mono text-[10px] tracking-[0.12em]">
              {expert.serial}
            </span>
          </div>
        </motion.article>
      </motion.div>
    </Wrapper>
  );
}

function CheckGlyph() {
  return (
    <svg width="7" height="7" viewBox="0 0 6 6" fill="none" aria-hidden="true">
      <path
        d="M0.8 3 L2.4 4.6 L5.2 1.4"
        stroke="var(--color-marketing-accent-green-dark)"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function BigQuoteGlyph() {
  return (
    <span
      aria-hidden="true"
      className="text-marketing-blue absolute -top-1 left-2 font-serif text-3xl leading-none select-none"
    >
      &ldquo;
    </span>
  );
}
